import "./styles.css";
import { LoadingButton } from "@mui/lab";
import { Box, Grid } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import MonthYearPicker from "../../../../shared/components/monthYearRangePicker";
import dayjs, { Dayjs } from "dayjs";
import useLazyQuery from "../../../../shared/components/hooks/useLazyQuery";
import NoDataFound from "../../../../shared/components/noDataFoundBanner";
import Loader from "../../../../shared/components/loader/linearProgress";
import html2canvas from "html2canvas";
import { useSearchParams } from "react-router-dom";

interface DocReportPropTypes {
  propertyId: string;
  reportSourceId: string;
}

interface FilterPropType {
  month: string | null;
  year: number | null;
  reportData?: any;
}

const DocReport: FC<DocReportPropTypes> = ({ propertyId, reportSourceId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportRef = useRef<HTMLDivElement>(null);

  const urlMonth = searchParams.get("month");
  const urlYear = searchParams.get("year");

  const [isInitialized, setIsInitialized] = useState(false);

  const [filter, setFilter] = useState<FilterPropType>({
    month: urlMonth || "",
    year: urlYear ? Number(urlYear) : 2025,
    reportData: "",
  });

  const {
    loading: reportFetchLoading,
    data: reportFetchData,
    fetchData: reportFetch,
  } = useLazyQuery(`/customer/doc-reports/get-report/by-filter`);

  useEffect(() => {
    if (propertyId) {
      reportFetch({
        params: {
          propertyId: propertyId,
          enabled: true,
        },
      });
    }
  }, [propertyId]);

  useEffect(() => {
    const data = reportFetchData as any;
    if (!data || data.length === 0) return;

    let selectedReport = null;

    // 1. Try to find report matching URL
    if (urlMonth && urlYear) {
      selectedReport = data.find(
        (item: any) => item.month === urlMonth && item.year === Number(urlYear),
      );
    }

    // 2. If no URL params OR (first load and URL mismatch), default to first item
    if (!urlMonth && !urlYear && !isInitialized) {
      const defaultReport = data[0];
      setSearchParams(
        { month: defaultReport.month, year: defaultReport.year.toString() },
        { replace: true },
      );
      setIsInitialized(true);
      return; // Let the next effect cycle handle the filter update via the new URL params
    }

    // 3. Update the filter state
    // If we found a report, show it.
    // If not, keep the month/year from URL but clear the reportData (shows NoDataFound)
    setFilter({
      month: urlMonth || data[0].month,
      year: urlYear ? Number(urlYear) : data[0].year,
      reportData: selectedReport ? selectedReport.reportData : "",
    });

    setIsInitialized(true);
  }, [reportFetchData, urlMonth, urlYear]);

  // 3. Update URL when MonthYearPicker changes
  const handleMonthYearChange = (date: Dayjs | null) => {
    if (date) {
      const newMonth = date.format("MMMM");
      const newYear = date.format("YYYY");

      // Update URL only - the useEffect above will handle updating the filter state
      setSearchParams({ month: newMonth, year: newYear });
    } else {
      setSearchParams({});
      setFilter({ month: null, year: null, reportData: "" });
    }
  };

  const selectedDate =
    filter?.month && filter?.year
      ? dayjs(`${filter?.year}-${dayjs(filter?.month, "MMMM").format("MM")}-01`)
      : null;

  const handleDownloadPdf = async () => {
    const rawTitle = (reportFetchData as any)?.[0]?.property?.title ?? "";

    const safeTitle = rawTitle.trim().replace(/\s+/g, "_");

    const fileName = `${safeTitle ? safeTitle + "_" : ""}performance-summary${
      filter?.year ? "_" + filter?.year + "_" : ""
    }${filter?.month ? "_" + filter?.month : ""}.pdf`;

    if (!filter?.reportData) return;

    const container = createPdfContainer(filter.reportData);

    await new Promise((r) => setTimeout(r, 100));

    const canvas = await html2canvas(container, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const pdf = new jsPDF("p", "mm", "a4", true);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    const imgData = canvas.toDataURL("image/png");

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(fileName);
    document.body.removeChild(container);

    // if (!reportRef.current) return;

    // const pdf = new jsPDF("p", "mm", "a4");

    // const pageWidth = pdf.internal.pageSize.getWidth();
    // const margin = 10;
    // const printableWidth = pageWidth - margin * 2;

    // await pdf.html(reportRef.current!, {
    //   margin: [10, 10, 10, 10],
    //   autoPaging: "text",
    //   html2canvas: {
    //     scale: 0.25,
    //   },
    //   width: printableWidth,
    //   windowWidth: reportRef.current!.scrollWidth,
    // });

    // pdf.save(fileName);

    // const margin = 10;
    // const pageWidth = pdf.internal.pageSize.getWidth() - margin * 2;
    // const pageHeight = pdf.internal.pageSize.getHeight() - margin * 2;

    // const imgWidth = pageWidth;
    // const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // let heightLeft = imgHeight;
    // let position = margin;

    // // First page
    // pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    // heightLeft -= pageHeight;

    // while (heightLeft > 0) {
    //   pdf.addPage();
    //   position = margin - (imgHeight - heightLeft);
    //   pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    //   heightLeft -= pageHeight;
    // }

    // pdf.save("report.pdf");
  };

  // const handleDownloadPdf = async () => {
  //   try {
  //     // Create a temporary div to hold the HTML content
  //     const tempDiv = document.createElement("div");
  //     tempDiv.innerHTML = filter?.reportData;

  //     // Apply some basic styling for better PDF appearance
  //     tempDiv.style.padding = "30px 80px";
  //     tempDiv.style.maxWidth = "800px";
  //     tempDiv.style.margin = "10 auto";

  //     // Add to document temporarily
  //     document.body.appendChild(tempDiv);

  //     // Convert HTML to canvas
  //     const canvas = await html2canvas(tempDiv, {
  //       scale: 2, // Higher scale for better quality
  //       useCORS: true,
  //       logging: false,
  //     });

  //     // Remove temporary element
  //     document.body.removeChild(tempDiv);

  //     // Calculate PDF dimensions
  //     const imgWidth = 210; // A4 width in mm
  //     const pageHeight = 297; // A4 height in mm
  //     const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //     // Create PDF
  //     const pdf = new jsPDF("p", "mm", "a4");
  //     let heightLeft = imgHeight;
  //     let position = 0;

  //     // Add image to PDF
  //     const imgData = canvas.toDataURL("image/png");
  //     pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);

  //     heightLeft -= pageHeight;

  //     // Add additional pages if content is too long
  //     while (heightLeft >= 0) {
  //       position = heightLeft - imgHeight;
  //       pdf.addPage();
  //       pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  //       heightLeft -= pageHeight;
  //     }

  //     // Download the PDF
  //     pdf.save("report.pdf");
  //   } catch (error) {
  //     console.error("Error generating PDF:", error);
  //     alert("Failed to generate PDF. Please try again.");
  //   } finally {
  //   }
  // };

  const createPdfContainer = (html: string) => {
    const el = document.createElement("div");
    el.style.position = "absolute";
    el.style.left = "-10000px";
    el.style.width = "794px"; // A4 Width
    el.style.padding = "40px";
    el.style.background = "#fff";

    el.innerHTML = `
    <style>
      * { box-sizing: border-box; font-family: Arial, sans-serif; }
      h3 { color: #000; margin-top: 20px; }
      p, li { color: #000; font-size: 14px; line-height: 1.5; }
      
      /* Essential List Styling for PDF */
      ul { padding-left: 20px !important; }
      li { list-style-position: outside !important; margin-bottom: 5px; }
      
      /* Quill Indentation for PDF */
      .ql-indent-1 { padding-left: 40px !important; list-style-type: circle !important; }
      .ql-indent-2 { padding-left: 80px !important; list-style-type: square !important; }
    </style>
    ${html}
  `;

    document.body.appendChild(el);
    return el;
  };

  function transformQuillList(html: string) {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    const listItems = temp.querySelectorAll("li");

    let currentParent: any = null;

    listItems.forEach((li) => {
      if (li.classList.contains("ql-indent-1")) {
        if (currentParent) {
          let subList = currentParent.querySelector("ul");
          if (!subList) {
            subList = document.createElement("ul");
            currentParent.appendChild(subList);
          }
          subList.appendChild(li);
        }
      } else {
        currentParent = li;
      }
    });

    return temp.innerHTML;
  }
  return (
    <>
      <Grid
        container
        className="doc-report-container"
        justifyContent="flex-end"
      >
        <Grid
          item
          xs={3}
          md={3}
          textAlign="end"
          alignItems="center"
          display="flex"
          justifyContent="center"
        >
          <MonthYearPicker
            value={selectedDate}
            onChange={handleMonthYearChange}
            // Optional: set filters if needed
            // setFilterData({ month: newDate?.month() + 1, year: newDate?.year() });
            // shouldDisableDate={shouldDisableDate}
            showClearIcon={false}
          />
        </Grid>
        <Grid
          item
          xs={3}
          md={3}
          textAlign="end"
          alignItems="center"
          display="flex"
          justifyContent="center"
        >
          <LoadingButton
            variant="contained"
            onClick={handleDownloadPdf}
            className="download-pdf-button"
          >
            Download PDF
          </LoadingButton>
        </Grid>
        <Grid item xs={12}>
          <Box className="doc-report-box">
            {reportFetchLoading ? (
              <Loader />
            ) : filter?.reportData ? (
              <div
                className="doc-report-div"
                ref={reportRef}
                dangerouslySetInnerHTML={{
                  __html: transformQuillList(filter?.reportData),
                }}
              />
            ) : (
              <NoDataFound />
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default DocReport;
